import { useEffect, useRef, useState } from "react";
import { Schedule } from "@/app/types/Schedule";
import { adminRoutes } from "../lib/api-client/adminRoutes";
import { publicRoutes } from "../lib/api-client/publicRoutes";
import { apiFetch } from "../lib/api-client/base";
import { useAdminData } from "../admin/context/AdminDataContext";

interface UseSchedulesOptions {
  mode: "admin" | "public";
  providerId: string;
  reloadKey?: number;
}

export function useSchedules({
  mode,
  providerId,
  reloadKey,
}: UseSchedulesOptions) {
  const ctx = useAdminData();

  // Track previous reloadKey so we only refresh on subsequent changes
  const prevReloadKeyRef = useRef(reloadKey);

  // Admin mode: data is loaded by the context; refresh when reloadKey changes
  useEffect(() => {
    if (!ctx || mode !== "admin" || !providerId) return;
    if (prevReloadKeyRef.current === reloadKey) return;
    prevReloadKeyRef.current = reloadKey;
    ctx.refreshSchedules(providerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // The context preloads schedules only for the provider currently selected on
  // the page. Asking for anyone else — booking on a colleague's behalf, say —
  // used to return an empty list forever, because nothing ever triggered the
  // fetch. That went unnoticed while every caller passed the selected provider.
  const schedulesForProvider = ctx?.schedules[providerId];
  const loadingForProvider = ctx?.loadingSchedules[providerId];
  useEffect(() => {
    if (!ctx || mode !== "admin" || !providerId) return;
    if (schedulesForProvider === undefined && !loadingForProvider) {
      ctx.refreshSchedules(providerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, providerId, schedulesForProvider, loadingForProvider]);

  if (ctx && mode === "admin") {
    return {
      schedules: ctx.schedules[providerId] ?? [],
      loading: ctx.loadingSchedules[providerId] ?? false,
      error: ctx.schedulesError[providerId] ?? null,
    };
  }

  // Public mode (or outside admin layout) — fetch locally
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [localSchedules, setLocalSchedules] = useState<Schedule[]>([]);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [localLoading, setLocalLoading] = useState(false);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [localError, setLocalError] = useState<string | null>(null);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!providerId) return;
    setLocalLoading(true);
    setLocalError(null);
    const load = async () => {
      try {
        const route =
          mode === "admin"
            ? adminRoutes.schedules.get(providerId)
            : publicRoutes.schedules.get(providerId);
        const result = await apiFetch(route);
        setLocalSchedules(result.items || []);
      } catch (err) {
        setLocalError(
          err instanceof Error ? err.message : "Failed to fetch schedules"
        );
        setLocalSchedules([]);
      } finally {
        setLocalLoading(false);
      }
    };
    load();
  }, [providerId, reloadKey]);

  return {
    schedules: localSchedules,
    loading: localLoading,
    error: localError,
  };
}
